import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkle, Check, X, PencilSimple, ArrowRight, ClipboardText, Warning, ListChecks, Archive, ArrowClockwise, Briefcase, Paperclip, CaretDown, CaretRight } from "@phosphor-icons/react";
import BriefingAudioStrip from "@/components/BriefingAudioStrip";
import AIContextPicker from "@/components/AIContextPicker";

/**
 * BriefingReviewDialog — 3-column review pane for a client_briefing /
 * engagement_briefing / meeting note. Source (left) · Transcript (middle) ·
 * Proposals (right). On mobile the three columns collapse into tabs.
 *
 * Governance contract:
 *   • Nothing writes to canonical client/engagement/playbook/task collections
 *     until the advisor explicitly clicks "Apply" on a proposal (or "Apply
 *     selected" on a task batch).
 *   • Every proposal card supports Accept / Edit / Reject and stamps a
 *     status pill so the advisor can see what is still open.
 *   • Applied proposals move to the "Applied" strip at the bottom for audit.
 */
const FIELD_LABELS = {
  strategic_context: "Strategic Context",
  known_priorities: "Known Priority",
  known_pain_points: "Pain Point",
  scope_summary: "Scope Summary",
  objectives: "Objective",
  key_risks: "Key Risk",
};

const CATEGORY_LABELS = {
  client_field: "Partner org field",
  engagement_field: "Engagement field",
  playbook_item: "Playbook proposal",
  deliverable: "Deliverable proposal",
  task: "Suggested task",
  decision: "Decision",
  follow_up: "Follow-up",
  open_question: "Open question",
  supporting_note: "Supporting note",
  engagement_seed: "Suggested engagement",
};

const PILL = {
  proposed: "border-brass/60 text-brass",
  applied: "border-emerald-500/60 text-emerald-500",
  rejected: "border-destructive/60 text-destructive line-through opacity-60",
};

function valueToText(item) {
  const v = item.edited_value != null ? item.edited_value : item.value;
  if (typeof v === "string") return v;
  if (v && typeof v === "object") {
    if (v.title && v.body) return `${v.title} — ${v.body}`;
    if (v.title) return v.title;
    if (v.prompt) return v.prompt;
    if (v.body) return v.body;
    return JSON.stringify(v);
  }
  return String(v ?? "");
}

function EditableValue({ item, onChange }) {
  const initial = item.edited_value != null ? item.edited_value : item.value;
  if (item.category === "task" && typeof initial === "object") {
    return (
      <div className="space-y-2">
        <Input
          data-testid={`proposal-edit-title-${item.id}`}
          value={initial.title || ""}
          onChange={(e) => onChange({ ...initial, title: e.target.value })}
          placeholder="Task title"
          className="rounded-none bg-secondary/40 text-sm"
        />
        <div className="grid grid-cols-2 gap-2">
          <Input
            data-testid={`proposal-edit-owner-${item.id}`}
            value={initial.owner || ""}
            onChange={(e) => onChange({ ...initial, owner: e.target.value })}
            placeholder="Owner"
            className="rounded-none bg-secondary/40 text-xs"
          />
          <Input
            data-testid={`proposal-edit-due-${item.id}`}
            type="date"
            value={initial.due || ""}
            onChange={(e) => onChange({ ...initial, due: e.target.value })}
            className="rounded-none bg-secondary/40 text-xs"
          />
        </div>
      </div>
    );
  }
  if (item.category === "supporting_note" && typeof initial === "object") {
    return (
      <div className="space-y-2">
        <Input
          data-testid={`proposal-edit-note-title-${item.id}`}
          value={initial.title || ""}
          onChange={(e) => onChange({ ...initial, title: e.target.value })}
          placeholder="Note title"
          className="rounded-none bg-secondary/40 text-sm"
        />
        <Textarea
          data-testid={`proposal-edit-note-body-${item.id}`}
          value={initial.body || ""}
          onChange={(e) => onChange({ ...initial, body: e.target.value })}
          rows={3}
          className="rounded-none bg-secondary/40 text-sm"
        />
      </div>
    );
  }
  if (item.category === "playbook_item" && typeof initial === "object") {
    return (
      <Textarea
        data-testid={`proposal-edit-prompt-${item.id}`}
        value={initial.prompt || ""}
        onChange={(e) => onChange({ ...initial, prompt: e.target.value })}
        rows={2}
        className="rounded-none bg-secondary/40 text-sm"
      />
    );
  }
  return (
    <Textarea
      data-testid={`proposal-edit-value-${item.id}`}
      value={typeof initial === "string" ? initial : valueToText(item)}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      className="rounded-none bg-secondary/40 text-sm"
    />
  );
}

function ProposalCard({ item, onPatch, onApply, applying }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.edited_value != null ? item.edited_value : item.value);

  const applied = item.status === "applied";
  const rejected = item.status === "rejected";

  const fieldLabel = item.field && FIELD_LABELS[item.field] ? FIELD_LABELS[item.field] : "";

  const save = async () => {
    await onPatch(item.id, { edited_value: { value: draft } });
    setEditing(false);
  };

  return (
    <div
      data-testid={`proposal-card-${item.id}`}
      className={`border p-3 space-y-2 ${applied ? "border-emerald-500/40 bg-emerald-500/5" : rejected ? "border-destructive/30 bg-destructive/5" : "border-brass/40 bg-brass-soft/40"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
          {CATEGORY_LABELS[item.category] || item.category}
          {fieldLabel && <span className="text-brass"> · {fieldLabel}</span>}
        </div>
        <span className={`text-[0.625rem] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${PILL[item.status] || ""}`} data-testid={`proposal-status-${item.id}`}>
          {item.status === "proposed" ? "In review" : item.status}
        </span>
      </div>

      {editing && !applied ? (
        <EditableValue item={{ ...item, edited_value: draft }} onChange={setDraft} />
      ) : (
        <div className="text-sm leading-relaxed whitespace-pre-wrap">{valueToText(item)}</div>
      )}

      {!applied && (
        <div className="flex gap-1.5 flex-wrap pt-1">
          {editing ? (
            <>
              <Button data-testid={`proposal-save-${item.id}`} onClick={save} size="sm" className="h-7 rounded-none bg-primary text-primary-foreground text-[0.6875rem]">
                <Check size={11} weight="bold" className="mr-1" /> Save edit
              </Button>
              <Button
                data-testid={`proposal-cancel-${item.id}`}
                onClick={() => { setEditing(false); setDraft(item.edited_value != null ? item.edited_value : item.value); }}
                variant="outline"
                size="sm"
                className="h-7 rounded-none text-[0.6875rem]"
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid={`proposal-accept-${item.id}`}
                onClick={() => onApply([item.id])}
                disabled={applying}
                size="sm"
                className="h-7 rounded-none bg-primary text-primary-foreground text-[0.6875rem]"
              >
                <Check size={11} weight="bold" className="mr-1" />
                {item.category === "engagement_seed" ? "Draft engagement briefing" : "Accept"}
              </Button>
              <Button
                data-testid={`proposal-edit-${item.id}`}
                onClick={() => setEditing(true)}
                variant="outline"
                size="sm"
                className="h-7 rounded-none text-[0.6875rem]"
              >
                <PencilSimple size={11} className="mr-1" /> Edit
              </Button>
              {!rejected ? (
                <Button
                  data-testid={`proposal-reject-${item.id}`}
                  onClick={() => onPatch(item.id, { status: "rejected" })}
                  variant="outline"
                  size="sm"
                  className="h-7 rounded-none text-[0.6875rem] border-destructive/60 text-destructive hover:bg-destructive/10"
                >
                  <X size={11} weight="bold" className="mr-1" /> Reject
                </Button>
              ) : (
                <Button
                  data-testid={`proposal-unreject-${item.id}`}
                  onClick={() => onPatch(item.id, { status: "proposed" })}
                  variant="outline"
                  size="sm"
                  className="h-7 rounded-none text-[0.6875rem]"
                >
                  Un-reject
                </Button>
              )}
            </>
          )}
        </div>
      )}
      {applied && item.applied_to && (
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-emerald-500 flex items-center gap-1">
          <Check size={10} weight="bold" /> Applied to {item.applied_to.kind}
          {item.applied_to.field ? ` · ${item.applied_to.field}` : ""}
        </div>
      )}
    </div>
  );
}

export default function BriefingReviewDialog({ open, onClose, note, onChanged }) {
  const [current, setCurrent] = useState(note);
  const [extracting, setExtracting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  // Iter 85.11 · Pre-flight Context Picker — partner-selectable
  // attachments + prior notes fed into the extraction payload. Iter
  // 85.16 · The picker itself is rendered via the shared
  // <AIContextPicker/> component and owns its own load state; we only
  // hold the picked-ids sets here so `extract()` can read them.
  const [pickedAttachments, setPickedAttachments] = useState(() => new Set());
  const [pickedPriorNotes, setPickedPriorNotes] = useState(() => new Set());

  useEffect(() => {
    setCurrent(note);
    setSelectedTasks(new Set());
    setPickedAttachments(new Set());
    setPickedPriorNotes(new Set());
  }, [note]);

  const items = current?.briefing_proposals?.items || [];
  const target = current?.briefing_target || null;
  // Kind precedence: explicit target.kind > note_type. Falls back to 'meeting'
  // so a note without a target (regular meeting) can still extract.
  const kind = target?.kind || current?.note_type || "meeting";
  const state = current?.briefing_state || "draft";

  const grouped = useMemo(() => {
    const g = { engagement_seed: [], client_field: [], engagement_field: [], playbook_item: [], task: [], supporting_note: [], decision: [], follow_up: [], open_question: [] };
    for (const it of items) {
      (g[it.category] = g[it.category] || []).push(it);
    }
    return g;
  }, [items]);

  const openTasks = grouped.task.filter((t) => t.status === "proposed");
  const openCount = items.filter((i) => i.status === "proposed").length;
  const appliedCount = items.filter((i) => i.status === "applied").length;

  const extract = async () => {
    // Extraction needs a target — for regular meeting notes we synthesize one
    // from the note's existing client/engagement links so the advisor never
    // has to pre-set anything to reach YadidAI.
    const effectiveTarget = target || {
      kind,
      client_id: current?.client_id || null,
      engagement_id: current?.engagement_id || null,
    };
    setExtracting(true);
    try {
      const contextPayload = (pickedAttachments.size || pickedPriorNotes.size)
        ? {
            attachment_ids: [...pickedAttachments],
            prior_note_ids: [...pickedPriorNotes],
          }
        : undefined;
      const { data } = await api.post(`/notes/${current.id}/briefing/extract`, {
        kind,
        target: effectiveTarget,
        ...(contextPayload ? { context: contextPayload } : {}),
      });
      setCurrent({ ...current, briefing_proposals: data.briefing_proposals, briefing_state: data.briefing_state, briefing_target: effectiveTarget });
      const proposed = data.briefing_proposals.items.filter(i => i.status === "proposed").length;
      const extras = (pickedAttachments.size || 0) + (pickedPriorNotes.size || 0);
      toast.success(
        extras
          ? `YadidAI staged ${proposed} proposals (with ${extras} extra context item${extras === 1 ? "" : "s"})`
          : `YadidAI staged ${proposed} proposals for review`
      );
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const patchProposal = async (proposalId, patch) => {
    try {
      const { data } = await api.patch(`/notes/${current.id}/briefing/proposals/${proposalId}`, patch);
      setCurrent((c) => ({
        ...c,
        briefing_proposals: {
          ...c.briefing_proposals,
          items: c.briefing_proposals.items.map((i) => (i.id === proposalId ? data.item : i)),
        },
      }));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const applyIds = async (ids) => {
    if (!ids.length) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/notes/${current.id}/briefing/apply`, { proposal_ids: ids });
      const errors = Object.entries(data.results || {}).filter(([, v]) => !v.ok);
      const okCount = Object.values(data.results || {}).filter((v) => v.ok && !v.already_applied).length;
      if (okCount) toast.success(`Accepted ${okCount} proposal${okCount === 1 ? "" : "s"}`);
      if (errors.length) toast.error(`${errors.length} could not be applied — ${errors[0][1].error || "check details"}`);
      // Refresh note snapshot from server for authoritative statuses.
      // Iter 85.31 · Preserve the pre-refresh `current` if the GET returns
      // an empty / null body. Previously an unexpected `undefined` here
      // was propagated into state and downstream <BriefingAudioStrip
      // note={current} /> crashed on `null.id`. The backend apply has
      // already committed atomically via db.notes.update_one, so a
      // failed re-hydrate is UI-cosmetic — surface a toast and keep the
      // pre-refresh view so the partner can close or refresh.
      try {
        const { data: fresh } = await api.get(`/notes/${current.id}`);
        if (fresh && fresh.id) {
          setCurrent(fresh);
        } else {
          console.warn("[BriefingReviewDialog] Post-apply GET returned empty body", { noteId: current.id, fresh });
          setCurrent((prev) => prev ? { ...prev, briefing_state: "applied" } : prev);
          toast.warning("Applied — refresh the page to see the latest state");
        }
      } catch (refetchErr) {
        console.warn("[BriefingReviewDialog] Post-apply GET failed", refetchErr);
        setCurrent((prev) => prev ? { ...prev, briefing_state: "applied" } : prev);
        toast.warning("Applied — refresh the page to see the latest state");
      }
      setSelectedTasks(new Set());
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const applySelectedTasks = () => applyIds([...selectedTasks]);

  const archive = async () => {
    try {
      await api.post(`/notes/${current.id}/briefing/archive`);
      setCurrent({ ...current, briefing_state: "archived" });
      toast.success("Briefing archived");
      onChanged?.();
    } catch (e) {
      toast.error("Archive failed");
    }
  };

  const toggleTaskSelect = (id) => {
    setSelectedTasks((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const SourcePane = () => {
    const onAudioChanged = (updated) => setCurrent((c) => (c ? { ...c, ...updated } : c));
    if (!current || !current.id) {
      return (
        <div className="text-xs text-muted-foreground italic p-3" data-testid="source-pane-loading">
          Loading source…
        </div>
      );
    }
    return (
      <div className="space-y-3">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Source material</div>
        <BriefingAudioStrip note={current} onChanged={onAudioChanged} />
        {current?.body && current.body !== "(Recorded meeting)" && current.body !== "(Briefing draft — record or paste material)" && (
          <div>
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Written briefing</div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed border border-border p-3 bg-card max-h-64 overflow-auto">
              {current.body}
            </div>
          </div>
        )}
      </div>
    );
  };

  const TranscriptPane = () => (
    <div className="space-y-2">
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Transcript</div>
      {current?.meeting_transcript ? (
        <div className="text-sm whitespace-pre-wrap leading-relaxed border border-border p-3 bg-card max-h-[420px] overflow-auto" data-testid="briefing-transcript">
          {current.meeting_transcript}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic">Transcribe audio to populate this pane.</div>
      )}
    </div>
  );

  const ProposalsPane = () => {
    // Iter 85.32 · Hard guard so ProposalsPane never dereferences a null
    // `current` while the post-apply GET is in flight. Iter 85.31 already
    // preserves `current` in `applyIds`, but a defensive guard here
    // eliminates the "Cannot read properties of null (reading 'id')"
    // crash class permanently regardless of parent lifecycle timing.
    if (!current || !current.id) {
      return (
        <div className="text-xs text-muted-foreground italic p-3" data-testid="proposals-pane-loading">
          Loading proposals…
        </div>
      );
    }
    return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
          Proposals · <span className={openCount ? "text-brass" : "text-muted-foreground"}>{openCount} in review</span>
          {appliedCount > 0 && <span className="text-emerald-500"> · {appliedCount} applied</span>}
        </div>
        <Button
          data-testid="briefing-extract-btn"
          onClick={extract}
          disabled={extracting}
          size="sm"
          className="h-8 rounded-none bg-primary text-primary-foreground text-[0.6875rem]"
        >
          <Sparkle size={12} weight="fill" className="mr-1" />
          {extracting ? "Reviewing…" : items.length ? "Re-review" : "Review with YadidAI"}
          {(pickedAttachments.size || pickedPriorNotes.size) ? (
            <span className="ml-1.5 opacity-90">
              · +{pickedAttachments.size + pickedPriorNotes.size}
            </span>
          ) : null}
        </Button>
      </div>

      {/* Iter 85.11/85.16 · Pre-flight Context Picker — shared with the
          meeting-note Organize flow via AIContextPicker. Kept collapsed
          by default so the fast path (transcript-only review) is one
          click away. Same test-id prefix ("briefing-context") to
          preserve the existing selectors. */}
      <AIContextPicker
        noteId={current.id}
        pickedAttachments={pickedAttachments}
        setPickedAttachments={setPickedAttachments}
        pickedPriorNotes={pickedPriorNotes}
        setPickedPriorNotes={setPickedPriorNotes}
        testIdPrefix="briefing-context-picker"
        onAttachmentUploaded={(a) => {
          // Iter 85.30 · Sync the fresh attachment into the briefing
          // dialog's local note state so the extract-context payload
          // stays consistent within this review session.
          setCurrent((c) => ({ ...c, attachments: [...(c.attachments || []), a] }));
        }}
      />

      {openTasks.length > 0 && (
        <div className="border border-brass/40 bg-brass-soft/40 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[0.6875rem] font-mono uppercase tracking-widest text-brass flex items-center gap-1.5">
              <ListChecks size={12} weight="bold" /> Suggested tasks
            </div>
            <Button
              data-testid="briefing-apply-selected-tasks"
              onClick={applySelectedTasks}
              disabled={applying || selectedTasks.size === 0}
              size="sm"
              className="h-7 rounded-none bg-primary text-primary-foreground text-[0.625rem]"
            >
              Create selected ({selectedTasks.size})
            </Button>
          </div>
          <div className="space-y-2">
            {openTasks.map((t) => (
              <label key={t.id} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`task-suggestion-${t.id}`}>
                <input
                  type="checkbox"
                  checked={selectedTasks.has(t.id)}
                  onChange={() => toggleTaskSelect(t.id)}
                  className="mt-1"
                  data-testid={`task-select-${t.id}`}
                />
                <div className="flex-1">
                  <div>{(t.edited_value?.title) || (t.value?.title) || "(untitled)"}</div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                    <ClipboardText size={9} weight="bold" className="inline mr-1" />
                    {t.value?.owner || "unassigned"} · due {t.value?.due || "—"}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {["engagement_seed", "client_field", "engagement_field", "playbook_item", "supporting_note", "decision", "follow_up", "open_question"].map((cat) => (
        grouped[cat]?.length > 0 && (
          <div key={cat} className="space-y-2">
            {grouped[cat].map((it) => (
              <ProposalCard key={it.id} item={it} onPatch={patchProposal} onApply={applyIds} applying={applying} />
            ))}
          </div>
        )
      ))}

      {items.length === 0 && !extracting && (
        <div className="border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
          Nothing reviewed yet. Click "Review with YadidAI" to stage proposals from the transcript and written briefing.
        </div>
      )}
    </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none bg-card max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="briefing-review-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <div>
              <DialogTitle className="font-display tracking-tight">{current?.title || "Briefing review"}</DialogTitle>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1">
                {kind.replace("_", " ")} · state: <span className="text-primary">{state.replace("_", " ")}</span>
              </div>
            </div>
            <div className="flex gap-2">
              {state !== "archived" && (
                <Button data-testid="briefing-archive-btn" onClick={archive} variant="outline" size="sm" className="rounded-none text-[0.6875rem] h-8">
                  <Archive size={11} weight="bold" className="mr-1" /> Archive
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Desktop 3-column */}
        <div className="hidden lg:grid grid-cols-3 gap-4 mt-2">
          <div className="border border-border p-3"><SourcePane /></div>
          <div className="border border-border p-3"><TranscriptPane /></div>
          <div className="border border-border p-3"><ProposalsPane /></div>
        </div>

        {/* Mobile tabs */}
        <div className="lg:hidden mt-2">
          <Tabs defaultValue="proposals">
            <TabsList className="rounded-none grid grid-cols-3">
              <TabsTrigger value="source" data-testid="briefing-tab-source" className="rounded-none text-xs">Source</TabsTrigger>
              <TabsTrigger value="transcript" data-testid="briefing-tab-transcript" className="rounded-none text-xs">Transcript</TabsTrigger>
              <TabsTrigger value="proposals" data-testid="briefing-tab-proposals" className="rounded-none text-xs">Proposals</TabsTrigger>
            </TabsList>
            <TabsContent value="source" className="border border-border p-3 mt-2"><SourcePane /></TabsContent>
            <TabsContent value="transcript" className="border border-border p-3 mt-2"><TranscriptPane /></TabsContent>
            <TabsContent value="proposals" className="border border-border p-3 mt-2"><ProposalsPane /></TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
